import { GenericExternalLink } from "@components/generic-external-link";
import { NoDataView } from "@components/title-slide-view";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { H1 } from "@components/utilities";
import { type GetOverviewPerformance_me_overviewPage } from "@north-beam/nb-client-side";
import { todayLocalAsISODate } from "@north-beam/nb-common";
import { FormattedDateRange } from "@shared/date-range-picker";
import { differenceInDays } from "@utils/datetime";
import { parseDateRangeArgument } from "@utils/index";

import { BreakdownView } from "./breakdown-view";
import { MetricCharts } from "./metric-charts";
import { type FetchReportParams } from "./overview";

export const AccrualPerformance = ({
  response: { performanceMetrics, performanceBreakdownTableRows },
  bodyState,
  breakdown,
  isLoading,
}: FetchReportParams<GetOverviewPerformance_me_overviewPage>) => {
  const { user } = useUser();
  const { attributionModel, dateRange: dr } = bodyState;
  const anchor = todayLocalAsISODate();
  const dateRange = parseDateRangeArgument(dr, anchor);
  const { startDate, endDate } = dateRange;
  const daysDiff = differenceInDays(startDate, endDate) + 1;

  let lowerSection = <NoDataView />;

  if (performanceMetrics && performanceBreakdownTableRows) {
    lowerSection = (
      <>
        <div
          className="grid grid-cols-2 grid-rows-2 gap-4 pb-4"
          data-test="performance-chart"
        >
          <MetricCharts
            metricsOverview={performanceMetrics}
            isLoading={isLoading}
          />
        </div>
        <div className="nb-breakdown-table" data-test="performance-breakdown">
          <BreakdownView
            breakdownTitle="Breakdown (Accrual performance)"
            breakdownSubtitle={`This table uses an infinite attribution window, and aggregates data over the last ${daysDiff} day(s)`}
            rows={performanceBreakdownTableRows}
            isLoading={isLoading}
            attributionWindow="infinity"
            attributionModel={attributionModel}
            breakdown={breakdown}
          />
        </div>
      </>
    );
  }

  const amEnum = user.attributionMethodEnum;

  return (
    <>
      <div className="py-4">
        <H1 className="flex-horizontal-center">
          Accrual performance{" "}
          <span className="ml-2 text-sm font-light bg-[#F8F8F9] py-0.5 px-3 border rounded-md">
            <FormattedDateRange dateRange={dateRange} />
          </span>
        </H1>
        <div className="nb-accounting-methods-short-explanation">
          This model attributes conversions on the{" "}
          <strong>day(s) that contributory marketing events took place</strong>,
          not the day that the conversion occurred. If multiple marketing events
          lead to a conversion, credit is divided among all contributing events,
          according to the attribution model selected.
        </div>
        <div className="nb-accounting-methods-read-more mt-2 flex-horizontal-center space-x-4">
          <span>
            <GenericTooltip
              className="mr-1"
              content={
                <>
                  Your current model is
                  <br />
                  <b>{amEnum.toReactSelect(attributionModel).label}</b>
                </>
              }
            />
            Attribution models{" "}
            <GenericExternalLink href="https://docs.northbeam.io/docs/attribution-models">
              read more
            </GenericExternalLink>
          </span>
          <span>
            Accrual accounting{" "}
            <GenericExternalLink href="https://docs.northbeam.io/docs/accounting-modes">
              read more
            </GenericExternalLink>
          </span>
        </div>
      </div>
      {lowerSection}
    </>
  );
};
